import * as React from "react";

import { cn } from "@/lib/utils";

type PaneHeaderProps = Omit<React.ComponentProps<"div">, "title"> & {
  title: string;
  /** Muted count after the title. */
  count?: number | null;
  /** Short status after the title, in place of `count`. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
};

/** The uppercase 11px label: pane titles and the IDE explorer's row labels. */
const paneTitleClass = "text-xs font-medium tracking-wide text-fg-3 uppercase";

/**
 * The 36px header of an IDE side pane: with the explorer rows, the one place
 * uppercase 11px labels are allowed. No fill; a `line-1` divider separates it
 * from the pane body.
 */
function PaneHeader({
  title,
  count,
  meta,
  actions,
  className,
  ...props
}: PaneHeaderProps) {
  return (
    <div
      data-slot="pane-header"
      className={cn(
        "flex h-9 min-w-0 shrink-0 items-center justify-between gap-2 border-b border-line-1 px-3",
        className
      )}
      {...props}
    >
      <span className="flex min-w-0 items-baseline gap-1.5" title={title}>
        <span className={cn("truncate", paneTitleClass)}>{title}</span>
        {meta ??
          (typeof count === "number" ? (
            <span className="text-xs text-fg-4 tabular-nums">{count}</span>
          ) : null)}
      </span>
      {actions ? (
        <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
      ) : null}
    </div>
  );
}

export { PaneHeader, paneTitleClass };
export type { PaneHeaderProps };
